import { useCallback, useEffect, useRef, useState } from "react";
import type { SessionInfo, TestInfo } from "../api";
import type { StageHandle } from "../components/Stage";
import { ResponseQueue } from "./presenter";
import type { RunnerEnv } from "./runner";
import { loadStation, runServerTest, screenGeometry, type StationContext } from "./testController";

export const EYE_INSTRUCTION: Record<string, string> = {
  OD: "Cover the LEFT eye. Test the right eye.",
  OS: "Cover the RIGHT eye. Test the left eye.",
  OU: "Keep both eyes open.",
};

export const COMPLIANCE_TEXT: Record<string, string> = {
  left_fullscreen: "the test left fullscreen",
  not_fullscreen: "the test could not run fullscreen",
  page_hidden: "the test window was hidden or minimised",
  lost_focus: "another window was used during the test",
  window_resized: "the window changed size",
  pixel_ratio_changed: "the screen scaling changed",
  color_depth_changed: "the screen's colour depth changed",
  moved_to_another_screen: "the window moved to another screen",
};

export function testTitle(t: TestInfo): string {
  const eye = { OD: "Right eye", OS: "Left eye", OU: "Both eyes" }[t.eye] ?? t.eye;
  return `${eye} — ${t.kind === "acuity" ? "acuity" : "contrast sensitivity"}`;
}

export function useRunner(session: SessionInfo | null) {
  const stage = useRef<StageHandle>(null);
  const responses = useRef(new ResponseQueue());
  const abortRef = useRef<AbortController | null>(null);
  const [station, setStation] = useState<StationContext | null>(null);
  const [stationError, setStationError] = useState<string | null>(null);
  const [geometryWarning, setGeometryWarning] = useState<string | null>(null);
  const [running, setRunning] = useState<TestInfo | null>(null);
  const [lastError, setLastError] = useState<string | null>(null);
  const [complianceReason, setComplianceReason] = useState<string | null>(null);

  useEffect(() => {
    if (!session) return;
    loadStation(session).then((s) => {
      setStation(s);
      setGeometryWarning(screenGeometry(s.display, session.viewing_distance_mm).warning);
    }, (e) => setStationError(e instanceof Error ? e.message : String(e)));
  }, [session?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => responses.current.attachKeyboard(window), []);

  const run = useCallback(async (test: TestInfo, onProgress?: RunnerEnv["progress"],
    onSink?: (s: { pending: number; error: string | null }) => void,
    opts: { requireFocus?: boolean; requireFullscreen?: boolean } = {}): Promise<TestInfo | null> => {
    const renderer = stage.current?.renderer;
    if (!session || !station || !renderer) throw new Error("display not ready");
    const ac = new AbortController();
    abortRef.current = ac;
    setRunning(test);
    setLastError(null);
    setComplianceReason(null);
    try {
      return await runServerTest({ session, test, station, renderer, responses: responses.current,
        signal: ac.signal, onProgress, onSink, ...opts, onCompliance: setComplianceReason });
    } catch (e) {
      setLastError(e instanceof Error ? e.message : String(e));
      return null;
    } finally {
      setRunning(null);
      abortRef.current = null;
    }
  }, [session, station]);

  const abort = useCallback(() => abortRef.current?.abort(), []);

  return { stage, responses: responses.current, station, stationError, geometryWarning, running, lastError,
    complianceReason, run, abort };
}
